import { createStore } from './store';
import isEqual from 'hs-lodash/isEqual';

/**
 * Creates a leader-follower pattern using Zustand stores with configurable sync strategies.
 *
 * The leader store will automatically publish updates via the configured sync strategy
 * when its value changes. The follower store will automatically receive
 * these updates and sync its state with the leader.
 *
 * Supported sync strategies:
 * - **Magpie**: For HubSpot internal communication (magpieStrategy)
 *
 * **Bidirectional Sync:**
 * - Followers can also send updates back to the leader via the configured sync strategy
 * - When a follower calls a method defined in the state creator or actions, the update is sent to the leader
 * - The leader processes the update and broadcasts it to all followers
 * - This creates a true bidirectional sync where any follower can update the shared state
 *
 * **Important Notes:**
 * - Subscription callbacks receive both the new value and previous value: `(newValue, previousValue) => void`
 * - State updates must be done through custom methods defined in the state creator
 * - This is so the actions can shared between the leader and follower stores as functions are not serializable
 * - Only one leader should be created per instance, but multiple followers are allowed
 * - All stores must be cleaned up when no longer needed to prevent memory leaks
 *
 * @param config - Configuration object containing:
 *   - strategy: Optional synchronization strategy (defaults to magpieStrategy)
 *   - actions: Optional factory function that receives `set` and `get` parameters and returns an object of action methods
 * @returns An object containing leader and follower factory functions
 *
 * @example
 * ```typescript
 * import * as t from 'ts-schema';
 * import { CollaborationSidebarAudience } from '../utils/magpie';
 * import { magpieStrategy } from './magpieStrategy';
 *
 * // Configure Magpie sync strategy with a schema for the state value
 * const syncStrategy = magpieStrategy<{ count: number }>({
 *   key: 'counter',
 *   audience: CollaborationSidebarAudience,
 *   valueSchema: t.object({ count: t.number }),
 * });
 *
 * // Create leader/follower stores
 * const { makeLeader, makeFollower } = createLeaderFollowerStore<{ count: number }>({
 *   syncStrategy,
 * });
 *
 * // Leader with initial state
 * const leader = makeLeader({ count: 0 });
 *
 * // Subscribe to leader changes (callback receives newValue, previousValue)
 * const unsubscribeLeader = leader.subscribe((newValue, previousValue) => {
 *   console.log('Leader value changed from', previousValue, 'to', newValue);
 * });
 *
 * // Follower
 * const follower = makeFollower();
 * console.log(follower.getState()); // Will automatically sync with leader
 *
 * // Subscribe to follower changes
 * const unsubscribeFollower = follower.subscribe((newValue, previousValue) => {
 *   console.log('Follower received:', newValue, 'was:', previousValue);
 * });
 *
 * // Cleanup when done
 * unsubscribeLeader();
 * unsubscribeFollower();
 * leader.cleanup();
 * follower.cleanup();
 * ```
 */

export function createLeaderFollowerStore(config) {
  const {
    syncStrategyFactory,
    actions: actionFactory
  } = config;
  const makeLeader = initialState => {
    const leaderStore = createStore(() => {
      return Object.assign({}, initialState);
    }, {
      devtools: {
        name: `${config.key}-leader-store`
      }
    });
    const syncStrategy = syncStrategyFactory.setupLeader({
      onNewLeaderValueRequest: () => leaderStore.getState(),
      onUpdateLeaderValueRequest: newValue => {
        leaderStore.setState(newValue, true);
      }
    });
    leaderStore.subscribe(newValue => {
      syncStrategy.reportNewLeaderValue(newValue);
    });
    const baseStore = {
      getState: leaderStore.getState,
      subscribe: leaderStore.subscribe,
      getInitialState: leaderStore.getInitialState,
      cleanup: () => {
        syncStrategy.cleanup();
      }
    };
    return actionFactory ? Object.assign({}, baseStore, {
      actions: actionFactory(leaderStore.setState, leaderStore.getState)
    }) : baseStore;
  };
  const makeFollower = initialState => {
    const followerStore = createStore(() => initialState, {
      devtools: {
        name: `${config.key}-follower-store`
      }
    });
    const readyStore = createStore(() => false);
    let isReadySubscription;
    const isReady = new Promise(resolve => {
      isReadySubscription = readyStore.subscribe(readyStoreState => {
        if (readyStoreState) {
          var _isReadySubscription;
          resolve();
          (_isReadySubscription = isReadySubscription) === null || _isReadySubscription === void 0 || _isReadySubscription();
        }
      });
    });
    const syncStrategy = syncStrategyFactory.setupFollower({
      onNewLeaderValue: newValue => {
        followerStore.setState(newValue, true);
        readyStore.setState(true);
      }
    });
    const proxyUpdateToLeader = (partial, replace) => {
      const currentState = followerStore.getState();
      followerStore.setState(partial, replace);
      const nextState = followerStore.getState();

      // A defensive measure to ensure the minimum number of updates are sent to the leader
      if (!isEqual(currentState, nextState)) {
        syncStrategy.requestUpdateLeaderValue(nextState);
      }
    };
    const baseStore = {
      getState: followerStore.getState,
      subscribe: followerStore.subscribe,
      getInitialState: followerStore.getInitialState,
      cleanup: () => {
        var _isReadySubscription2;
        syncStrategy.cleanup();
        (_isReadySubscription2 = isReadySubscription) === null || _isReadySubscription2 === void 0 || _isReadySubscription2();
      },
      isReady
    };
    return actionFactory ? Object.assign({}, baseStore, {
      actions: actionFactory(proxyUpdateToLeader, followerStore.getState)
    }) : baseStore;
  };
  return {
    makeLeader,
    makeFollower
  };
}